<template>
  <page-container
    :loading="isLoading || !presenceLoaded || securityChecking"
    :not-found="isError"
    :custom-error="accessDenied ? $t('linkUnavaliable') : null"
  >
    <div class="lesson" v-if="!isError && !isLoading">
      <ViewerWelcomeDialog
        v-if="!initiated && !isLoading && !isTeacher && !isEnded"
        :viewer="{ ...viewer, online: presence[viewer.id] }"
        @submit="initiate"
      />
      <TeacherGoodbyeDialog
        @submit="initiate"
        :test-only="Object.keys(lesson.viewers).length < 4"
        ref="TeacherGoodbyeDialog"
      />
      <v-container class="py-0" fluid v-if="!accessDenied">
        <v-row class="lesson__wrapper">
          <div class="lesson__container" align="center">
            <div class="lesson__session">
              <v-row class="lesson__meta px-6">
                <v-col align="start" cols="3" class="lesson__header">
                  <logo
                    :class="{ 'lesson__logo--active': !isLive && isTeacher }"
                    @click="!isLive && isTeacher ? $router.push('/home') : null"
                    class="lesson__logo"
                  ></logo>
                </v-col>
                <v-col align="start" cols="6" class="lesson__header">
                  <h3 class="text-center">{{ lesson.title }}</h3>
                  <p class="smaller lighten text-center">{{ startDate }}</p>
                </v-col>
                <v-col align="start" cols="3" class="lesson__header--windowChange">
                  <lesson-view-change-button
                    :items="viewTypes"
                    :selected="savedWindowsMode"
                    v-if="isTeacher"
                    class="ms-0"
                    @select="changeWindowsMode"
                  ></lesson-view-change-button>
                </v-col>
              </v-row>
              <v-row class="lesson__windows">
                <!-- COMPONENT: WINDOW -->
                <v-col
                  align="center"
                  v-for="(window, index) in lesson.windows"
                  class="lesson__window"
                  :class="`lesson__window--${windowMode(index)}`"
                  :key="index"
                >
                  <!-- eslint-disable-next-line max-len -->
                  <lesson-window
                    :type="window.type"
                    :participant="window.participant"
                    :live="isLive"
                    :loading="getConnectionStatus(index)"
                    :viewer="viewer"
                    :index="index"
                    :room="getRoomId(index)"
                    :page="window.page"
                    :link="window.link"
                    :name="selectedMedia ? selectedMedia.name : null"
                    @reconnect="reconnect"
                    @numpages="setKeynoteLength"
                  ></lesson-window>
                </v-col>
                <!-- COMPONENT: WINDOW -->
              </v-row>
            </div>
            <div class="lesson__remote">
              <v-row class="lesson__remote--row">
                <v-col align="start" cols="6" class="lesson__menu">
                  <v-row>
                    <div class="lesson__icon-button">
                      <!-- eslint-disable-next-line max-len -->
                      <LessonResourcesDialog
                        ref="LessonResourcesDialog"
                        :media="lesson.media"
                        :windows="lesson.windows"
                      />
                      <button-circular
                        class="me-4"
                        :icon="'mdi-forum-outline'"
                        v-if="isLive"
                        :tooltip="$t('chat')"
                        @click="toggleChat"
                      ></button-circular>

                      <button-cta
                        class="lesson__icon-button-start"
                        :lesson-data="lesson"
                        v-if="!isEnded"
                      ></button-cta>
                      <button-circular
                        class="ms-4"
                        :icon="`mdi-monitor${screenSharing ? '-off' : ''}`"
                        v-if="isTeacher && isLive"
                        :tooltip="$t('shareScreen')"
                        @click="toggleShareScreen"
                      ></button-circular>
                    </div>
                  </v-row>
                </v-col>
                <v-col align="start" cols="6" class="lesson__menu">
                  <v-row>
                    <!-- COMPONENT: CIRCULAR BUTTON -->
                    <div class="lesson__icon-button">
                      <button-circular
                        :icon="'mdi-folder'"
                        class="ms-0"
                        :tooltip="$t('media')"
                        @click="$refs.LessonResourcesDialog.open()"
                      ></button-circular>
                      <LessonResourcesDialog
                        ref="LessonResourcesDialog"
                        :media="lesson.media"
                        :windows="lesson.windows"
                      />
                      <div v-if="isTeacher">
                        <button-dropdown
                          :media="lesson.media"
                          :selected="selectedMedia"
                          class="ms-0"
                          @select="selectMedia"
                          @unselect="unselectMedia"
                        ></button-dropdown>
                      </div>
                      <button-circular
                        v-if="keynoteVisible && isTeacher"
                        :icon="'mdi-chevron-left'"
                        :tooltip="$t('keynotePrev')"
                        @click="keynoteNavigate('prev')"
                      ></button-circular>
                      <button-circular
                        v-if="keynoteVisible && isTeacher"
                        :icon="'mdi-chevron-right'"
                        :tooltip="$t('keynoteNext')"
                        @click="keynoteNavigate('next')"
                      ></button-circular>
                    </div>
                    <!-- COMPONENT: CTA BUTTON -->
                  </v-row>
                </v-col>
              </v-row>
            </div>
          </div>
          <!-- COMPONENT: SIDEBAR -->
          <v-col cols="12" md="3" class="lesson__sidebar fill-height pl-0 pt-12">
            <div class="lesson__sidebar-header">
              <h3 class="ml-4" style="width:50%;">{{ totalStatus }}</h3>
              <p class="ml-4 lesson__sidebar-text" style="width:50%;">
                <a
                  href="https://podpora.ucime.se/"
                  target="_blank"
                  style="text-decoration:none;"
                >{{ $t('support') }}</a>
              </p>
            </div>
            <v-divider class="my-4"></v-divider>
            <div class="lesson__viewers-group px-4">
              <p class="text-left text-uppercase lighten smaller">{{ $t('online') }}</p>
              <div v-if="onlineViewers.length">
                <viewer-item
                  v-for="value in onlineViewers"
                  :key="value.id"
                  :viewer="{ ...value, online: presence[value.id] }"
                  :lesson="lesson"
                  @connect="connectViewer"
                  @disconnect="disconnectViewer"
                ></viewer-item>
              </div>
              <no-viewers :message="'online'" v-else></no-viewers>
            </div>
            <div class="lesson__viewers-group px-4 pt-8 offline-users">
              <p class="text-left text-uppercase lighten smaller">{{ $t('offline') }}</p>
              <div v-if="offlineViewers.length">
                <viewer-item
                  v-for="value in offlineViewers"
                  :key="value.id"
                  :viewer="{ ...value, online: presence[value.id] }"
                  :lesson="lesson"
                  @connect="connectViewer"
                  @disconnect="disconnectViewer"
                ></viewer-item>
              </div>
              <no-viewers :message="'offline'" v-else-if="onlineViewers.length > 0"></no-viewers>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </page-container>
</template>

<script>
import moment from 'moment';
import pullAt from 'lodash/pullAt';
import clone from 'lodash/clone';
import { orderBy } from 'lodash';
import pageContainer from '@/components/PageContainer.vue';
import viewerItem from '@/components/ViewerItem.vue';
import noViewers from '@/components/NoViewers.vue';
import buttonCircular from '@/components/ButtonCircular.vue';
import lessonWindow from '@/components/LessonWindow.vue';
import logo from '@/assets/logo.svg';
import { db, rtdb } from '@/db';
import { mapGetters } from 'vuex';
import ButtonCta from '../components/ButtonCta.vue';
import ButtonDropdown from '../components/ButtonDropdown.vue';
import LessonResourcesDialog from '../components/LessonResourcesDialog.vue';
import ViewerWelcomeDialog from '../components/ViewerWelcomeDialog.vue';
import TeacherGoodbyeDialog from '../components/TeacherGoodbyeDialog.vue';
import LessonViewChangeButton from '../components/LessonViewChangeButton.vue';

export default {
  components: {
    ButtonCta,
    ButtonDropdown,
    pageContainer,
    viewerItem,
    logo,
    noViewers,
    buttonCircular,
    lessonWindow,
    LessonResourcesDialog,
    ViewerWelcomeDialog,
    TeacherGoodbyeDialog,
    LessonViewChangeButton,
  },
  async created() {
    /* is on teacher's link but is unauthenticated */
    if (this.isTeacher && !this.isAuthenticated) {
      await this.createViewer();
    }
    this.subscribePresence();
    this.subscribeAudioAvailability();
    setTimeout(() => {
      this.timeoutLapsed = true;
    }, this.timeout);
    if (!this.isTeacher) {
      this.$store.dispatch('logEvent', {
        name: 'viewer_loaded_lesson',
      });
    }
  },
  metaInfo() {
    return {
      title: this.$t('projectTitle'),
      titleTemplate: `${
        this.lesson && this.lesson.title ? this.lesson.title : '%s'
      }`,
    };
  },
  computed: {
    ...mapGetters(['auth']),
    savedWindowsMode() {
      let mode = 0;
      if (
        this.lesson.windows[0].mode &&
        this.lesson.windows[0].mode === 'even' &&
        this.lesson.windows[1].mode &&
        this.lesson.windows[1].mode === 'even'
      ) {
        mode = 0;
      } else if (
        this.lesson.windows[0].mode &&
        this.lesson.windows[0].mode === 'filled' &&
        this.lesson.windows[1].mode &&
        this.lesson.windows[1].mode === 'floating'
      ) {
        mode = 1;
      } else if (
        this.lesson.windows[0].mode &&
        this.lesson.windows[0].mode === 'floating' &&
        this.lesson.windows[1].mode &&
        this.lesson.windows[1].mode === 'filled'
      ) {
        mode = 2;
      }
      return mode;
    },
    isError() {
      return (
        !this.isLoading && (!this.lesson || (!this.isTeacher && !this.viewer))
      );
    },
    viewerId() {
      return this.$route.params.viewer || this.auth.email;
    },
    selectedMedia() {
      // eslint-disable-next-line arrow-body-style
      const links = this.lesson.media.links.map((l) => {
        return { name: l, link: l };
      });
      const allMedia = [...links, ...this.lesson.media.files];
      return allMedia.find(m => m.link === this.lesson.windows[1].link);
    },
    edgeWindows() {
      return pullAt(
        [...this.lesson.windows],
        [0, this.lesson.windows.length - 1],
      );
    },
    keynoteWindow() {
      return this.edgeWindows.find(w => w.type === 'keynote') || {};
    },
    keynoteVisible() {
      return this.edgeWindows.some(w => w.type === 'keynote');
    },
    lessonId() {
      return this.$route.params.lesson;
    },
    viewerEmail() {
      return this.isTeacher ? this.auth.email : this.viewer.email;
    },
    isLive() {
      return this.lesson.status === 'live';
    },
    isEnded() {
      return this.lesson.status === 'ended';
    },
    viewer() {
      return !this.isTeacher && !this.isLoading
        ? this.lesson.viewers[this.$route.params.viewer]
        : null;
    },
    isAuthenticated() {
      return !!this.auth;
    },
    isTeacher() {
      return !this.$route.params.viewer;
    },
    isParticipant() {
      return (
        // eslint-disable-next-line operator-linebreak
        this.isStudentBroadcasting &&
        this.lesson.windows[0].participant === this.viewerEmail
      );
    },
    isStudentBroadcasting() {
      return this.windows[1].type === 'stream';
    },
    totalStatus() {
      return `${this.$t('students')} ${this.onlineViewers.length} / ${
        this.viewers.length
      }`;
    },
    viewers() {
      const viewers = Object.keys(this.lesson.viewers).map(key => ({
        ...this.lesson.viewers[key],
        id: key,
      }));
      return orderBy(
        viewers,
        ['connected', 'waving', 'name', 'email'],
        ['desc', 'asc', 'desc', 'desc'],
      );
    },
    isLoading() {
      return this.lesson === 'loading' || !this.timeoutLapsed || !this.presence;
    },
    startDate() {
      return moment(this.lesson.start.toDate())
        .locale('cs')
        .format('dddd D. M. YYYY [od] hh:mm');
    },
    onlineViewers() {
      const onlineViewers = this.viewers.filter(
        viewer => this.presence[viewer.id],
      );
      return orderBy(
        onlineViewers,
        ['waving', 'name', 'email'],
        ['asc', 'desc', 'desc'],
      );
    },
    offlineViewers() {
      const offlineViewers = this.viewers.filter(
        viewer => !this.presence[viewer.id],
      );
      return orderBy(
        offlineViewers,
        ['waving', 'name', 'email'],
        ['asc', 'desc', 'desc'],
      );
    },
    viewersWindow() {
      return this.lesson.windows.find(
        w =>
          // eslint-disable-next-line implicit-arrow-linebreak,operator-linebreak
          w.participant ===
          (this.isTeacher ? this.auth.email : this.viewer.email),
      );
    },
  },
  data() {
    return {
      interval: null,
      screenSharing: false,
      keynoteLength: {},
      accessDenied: false,
      lesson: 'loading',
      initiated: false,
      timeout: 500,
      timeoutLapsed: false,
      presence: {},
      presenceLoaded: false,
      securityChecking: false,
      viewTypes: [],
    };
  },
  watch: {
    initiated(val) {
      if (val && !this.isTeacher) {
        this.executeCommandFiveTimes('displayName', this.viewer.name);
        this.executeCommandFiveTimes(
          'avatarUrl',
          `https://ucime-se.attendu.cz/static/uploads/ucime-se/${this.viewer.avatar}.png`,
        );
        // viewer is not connected => disconnect
        this.changeAudioStatus(
          this.viewer.connected ? 'connect' : 'disconnect',
        );
      }
    },
    lesson: {
      handler(val, prev) {
        // teacher setup
        if (!this.isTeacher) {
          // is freshly loaded
          if (prev !== 'loading') {
            const currViewer = val.viewers[this.viewerId];
            const prevViewer = prev.viewers[this.viewerId];
            // disconnect status changed
            if (prevViewer.connected !== currViewer.connected) {
              this.changeAudioStatus(
                currViewer.connected ? 'connect' : 'disconnect',
              );
              // name changed
            } else if (prevViewer.name !== currViewer.name) {
              this.executeCommand('displayName', currViewer.name);
            }
          }
        } else if (prev === 'loading') {
          if (this.auth.email !== val.author) {
            this.createViewer();
          }
          this.initTeacher();
        } else if (prev.status === 'live' && val.status === 'ended') {
          this.$refs.TeacherGoodbyeDialog.open();
        }
      },
      deep: true,
    },
  },
  methods: {
    pullAt,
    removedLastChar(str) {
      return str.substring(0, str.length - 1);
    },
    reconnect() {
      this.executeCommandFiveTimes('connectSpeaker');
    },
    async createViewer() {
      this.securityChecking = true;
      await this.$store
        .dispatch('viewerCreate', {
          lesson: this.$route.params.lesson,
        })
        .then(res => {
          window.location.href = `http://${window.location.host}/lesson/${this.lessonId}/${res.data}`;
        })
        .catch(() => {
          this.accessDenied = true;
          this.securityChecking = false;
        });
    },
    subscribeAudioAvailability() {
      if (!this.isTeacher) {
        /* clean voiceless status */
        this.$store
          .dispatch('viewerUpdate', {
            viewer: this.$route.params.viewer,
            value: false,
            action: 'voiceless',
            lesson: this.$route.params.lesson,
          })
          .then(() => {
            window.onmessage = e => {
              const { data } = e;
              if (data === 'audio--unavailable') {
                /* set as voiceless on mic error */
                this.$store.dispatch('viewerUpdate', {
                  viewer: this.$route.params.viewer,
                  value: true,
                  action: 'voiceless',
                  lesson: this.$route.params.lesson,
                });
              }
            };
          });
      }
    },
    changeAudioStatus(status) {
      this.executeCommand(`${status}Audio`);
      let count = 0;
      if (this.interval) clearInterval(this.interval);
      this.interval = setInterval(() => {
        this.executeCommand(`${status}Audio`);
        if (count === 4) clearInterval(this.interval);
        count += 1;
      }, 3000);
    },
    executeCommandFiveTimes(command, value = null) {
      this.executeCommand(command, value);
      let count = 0;
      const interval = setInterval(() => {
        this.executeCommand(command, value);
        if (count === 4) clearInterval(interval);
        count += 1;
      }, 3000);
    },
    initTeacher() {
      setTimeout(() => {
        this.executeCommandFiveTimes('displayName', this.$t('teacherMulti'));
        this.executeCommandFiveTimes(
          'avatarUrl',
          'https://ucime-se.attendu.cz/static/uploads/ucime-se/owl.png',
        );
      }, 5000);
    },
    toggleChat() {
      this.executeCommand('toggleChat');
      this.$store.dispatch('logEvent', {
        name: 'viewer_toggled_chat',
      });
    },
    toggleShareScreen() {
      this.screenSharing = !this.screenSharing;
      this.executeCommand('toggleShareScreen');
      this.$store.dispatch('logEvent', {
        name: 'viewer_toggled_screen_share',
      });
    },
    executeCommand(type, value) {
      if (this.isLive) {
        const iframe = document.getElementsByTagName('iframe')[0];
        let win = null;
        try {
          win = iframe.contentWindow;
          win.postMessage({ type, value }, '*');
        } catch (e) {
          win = iframe.contentWindow;
          win.postMessage({ type, value }, '*');
        }
      }
    },
    selectMedia(file) {
      const newWindows = [...this.lesson.windows];
      const windowIndex = newWindows.findIndex(w => w.type === 'keynote');

      const newWindow = {
        page: 1,
        link: file.link,
        mode: newWindows[windowIndex].mode || 'even',
        type: 'keynote',
        mediaType: file.type,
      };
      newWindows[windowIndex] = newWindow;

      this.$store.dispatch('updateDb', {
        type: 'lessons',
        id: this.$route.params.lesson,
        item: 'windows',
        value: newWindows,
      });
      this.$store.dispatch('logEvent', {
        name: 'user_changed_media',
      });
    },
    changeWindowsMode(selectedIndex) {
      const newWindows = [...this.lesson.windows];
      if (selectedIndex === 0) {
        newWindows[0].mode = 'even';
        newWindows[1].mode = 'even';
      } else if (selectedIndex === 1) {
        newWindows[0].mode = 'filled';
        newWindows[1].mode = 'floating';
      } else if (selectedIndex === 2) {
        newWindows[0].mode = 'floating';
        newWindows[1].mode = 'filled';
      }
      this.$store.dispatch('updateDb', {
        type: 'lessons',
        id: this.$route.params.lesson,
        item: 'windows',
        value: newWindows,
      });
    },
    windowMode(index) {
      return this.lesson.windows[index].mode || 'even';
    },
    unselectMedia() {
      const newWindows = [...this.lesson.windows];
      const windowIndex = newWindows.findIndex(w => w.type === 'keynote');
      newWindows[windowIndex] = { page: 1, link: '', type: 'keynote' };
      this.$store.dispatch('updateDb', {
        type: 'lessons',
        id: this.$route.params.lesson,
        item: 'windows',
        value: newWindows,
      });
    },
    subscribePresence() {
      let firstBinding = true;
      const lessonPresenceDatabaseRef = rtdb.ref(`${this.lessonId}`);
      return lessonPresenceDatabaseRef.on('value', snapshot => {
        if (snapshot.val() === false) {
          return;
        }
        if (snapshot.val()) this.presence = clone(snapshot.val());
        if (firstBinding) {
          if (!this.isTeacher && snapshot.val()[this.viewerId] === true) {
            this.$router.push(`/lesson/${this.lesson.id}`);
          }
          this.setPresence();
        }
        this.presenceLoaded = true;
        firstBinding = false;
      });
    },
    setPresence() {
      if (!this.isTeacher && !this.accessDenied) {
        rtdb.ref('.info/connected').on('value', snapshot => {
          if (snapshot.val() === false) {
            return;
          }
          const viewerStatusDatabaseRef = rtdb.ref(
            `${this.lessonId}/${this.viewerId}`,
          );
          viewerStatusDatabaseRef
            .onDisconnect()
            .set(false)
            .then(() => {
              viewerStatusDatabaseRef.set(true);
            });
        });
      }
    },
    setKeynoteLength(num) {
      this.keynoteLength = num;
    },
    keynoteNavigate(type) {
      const newWindows = [...this.lesson.windows];
      const index = newWindows.findIndex(w => w.type === 'keynote');
      if (newWindows[index].link) {
        if (type === 'next' && newWindows[index].page < this.keynoteLength) {
          newWindows[index].page += 1;
        } else if (type === 'prev' && newWindows[index].page > 1) {
          newWindows[index].page -= 1;
        }
        this.$store.dispatch('updateDb', {
          type: 'lessons',
          id: this.$route.params.lesson,
          item: 'windows',
          value: newWindows,
        });
      }
    },
    getRoomId(index) {
      return `ucime-se_${this.lessonId}-${index}`;
    },
    getConnectionStatus(index) {
      return this[`connecting${index.toString()}`];
    },
    lastChar(str) {
      return str.substring(str.length - 1, str.length);
    },
    pause() {
      if (this.isTeacher) this.teacherPause();
      else this.viewerPause();
    },
    teacherPause() {
      // eslint-disable-next-line max-len
      const newWindows = this.lesson.windows.map(
        // eslint-disable-next-line no-confusing-arrow
        w =>
          // eslint-disable-next-line implicit-arrow-linebreak
          w.participant === this.auth.email ? { ...w, camera: !w.camera } : w,
        // eslint-disable-next-line function-paren-newline
      );
      this.$store.dispatch('updateDb', {
        type: 'lessons',
        id: this.$route.params.lesson,
        item: 'windows',
        value: newWindows,
      });
    },
    viewerPause() {
      this.$store.dispatch('viewerUpdate', {
        viewer: this.$route.params.viewer,
        value: !this.viewersWindow.camera,
        action: 'camera',
        lesson: this.$route.params.lesson,
      });
    },
    initiate() {
      this.initiated = true;
    },
    connectViewer(viewer) {
      this.$store.dispatch('updateDb', {
        type: 'lessons',
        id: this.$route.params.lesson,
        item: `viewers.${viewer.id}.connected`,
        value: true,
      });
      this.$store.dispatch('logEvent', {
        name: 'user_connected_viewer',
      });
    },
    disconnectViewer(viewer) {
      this.$store.dispatch('updateDb', {
        type: 'lessons',
        id: this.$route.params.lesson,
        item: `viewers.${viewer.id}.connected`,
        value: false,
      });
      this.$store.dispatch('logEvent', {
        name: 'user_disconnected_viewer',
      });
    },
    updateWindows(value) {
      this.$store.dispatch('updateDb', {
        type: 'lessons',
        id: this.$route.params.lesson,
        item: 'windows',
        value,
      });
    },
  },
  firestore() {
    return {
      lesson: db.collection('lessons').doc(this.$route.params.lesson),
    };
  },
};
</script>

<style lang="scss">
.row {
  margin: 0 !important;
}

.container {
  padding: 0px !important;
}

.lesson {
  &__wrapper {
    height: 100vh;
    flex-direction: row;
    background: #f5f5fb;
    @media (max-width: 1024px) {
      flex-direction: row;
    }
  }

  &__sidebar-header {
    display: flex;
    flex-direction: row;
  }
  &__sidebar-text {
    margin-right: 30px;
    text-align: right;
  }
  &__meta {
    position: absolute;
    width: 75%;
    background-color: #f5f5fb;
    padding-top: 10px;
    z-index: 4;
    @media (max-width: 1024px) {
      flex-direction: column;
      width: 100% !important;
      position: inherit !important;
      align-items: center;
    }
  }
  &__header {
    margin: auto 0;
    @media (max-width: 1024px) {
      flex: 0 0 100%;
      max-width: 100%;
      text-align: center;
    }
    &--windowChange {
      @media (max-width: 1024px) {
        display: none !important;
      }
    }
  }
  &__logo {
    width: 100px;
    cursor: not-allowed;
    &--active {
      cursor: pointer;
    }
  }
  &__menu {
    padding: 0 !important;
    @media (max-width: 1024px) {
      width: 100% !important;
      min-width: 100% !important;
    }
  }
  &__sidebar {
    background: #ffffff;
    overflow: scroll;
    border-left: 1px solid #e8e8e8;
    @media (max-width: 1024px) {
      padding-bottom: 200px;
    }
  }
  &__container {
    overflow-x: hidden;
    width: 75%;
    @media (max-width: 1024px) {
      padding-left: 0px !important;
      padding-right: 0px !important;
      width: 100%;
    }
  }
  &__windows {
    flex-direction: row;
    @media (max-width: 1024px) {
      flex-direction: column;
      width: 100vw;
    }
  }
  &__window-controls {
    margin-top: 22vw;
    @media (max-width: 1024px) {
      margin-top: 45.5vw;
    }
  }
  &__window {
    height: 480px;
    background: #ffffff;
    margin: 0 25px;
    padding: 0;
    margin-top: 9vh;
    @media (max-width: 1024px) {
      margin: 0 !important;
    }
    @media only screen and (max-width: 1468px) {
      height: 360px !important;
    }
    @media (min-width: 1025px) {
      &--filled {
        z-index: 1;
        width: 75% !important;
        margin: 0 !important;
        height: 80vh !important;
        margin-top: 10vh !important;
        iframe {
          height: 80vh !important;
        }
        .lesson-window-alert {
          height: 80vh !important;
        }
      }
      &--floating {
        position: absolute;
        width: 200px;
        left: 0;
        z-index: 2;
        margin-top: 16vh;
        padding-top: 0px !important;
        height: auto !important;
        min-height: 100px;
        iframe {
          height: auto !important;
        }
        .lesson-window {
          padding-bottom: 0vh !important;
        }
        .lesson-window-alert {
          height: 190px !important;
        }
      }
      &--even {
        .lesson-window-alert {
          height: 480px !important;
        }
      }
    }
  }
  &__remote {
    position: fixed;
    bottom: 0;
    width: 75%;
    background-color: #fff;
    border-top: 1px solid #e8e8e8;
    z-index: 3;
    @media (max-width: 1024px) {
      flex-direction: column;
      width: 100%;
      z-index: 10;
      height: auto;
    }
    &--row {
      margin: 0px !important;
      @media (max-width: 1024px) {
        flex-direction: column;
        padding-top: 20px;
      }
    }
  }
  &__icon-button {
    display: flex;
    flex-direction: row;
    padding: 22px;
    margin: 0 auto;
    @media (max-width: 1430px) {
      padding-left: 4px !important;
      padding-right: 4px !important;
    }
  }
  @media (max-width: 1024px) {
    padding: 0;
  }
}

.textLayer {
  height: 120% !important;
}

.offline-users {
  @media (max-width: 1024px) {
    padding-bottom: 210px !important;
  }
}

.lesson-window {
  padding-bottom: 9vh;
}
</style>
